import React from "react";
import styles from "./About.module.css";
import prose from "../../styles/prose.module.css";
import BusinessIcon from "../../assets/Business.svg";
import FamilyIcon from "../../assets/Family.svg";
import YouthIcon from "../../assets/Youth.svg";
import MdImage from "../../assets/md.png";
import SectionHeading from "../../ReusableComponents/SectionHeading/SectionHeading";
import Reveal from "../../ReusableComponents/Reveal/Reveal";

const pillars = [
  {
    icon: BusinessIcon,
    title: "Business Leadership",
    desc: "Diversified ventures creating economic opportunities",
  },
  {
    icon: FamilyIcon,
    title: "Family Values",
    desc: "Grounded in compassion and human connection",
  },
  {
    icon: YouthIcon,
    title: "Youth Empowerment",
    desc: "Investing in the next generation's potential",
  },
];

/**
 * The bio reads as four distinct movements - the person, the businessman, the
 * philosophy, the family man. They were four undifferentiated paragraphs, so
 * the structure was invisible. Kickers make it legible; the prose itself is
 * unchanged.
 */
const MOVEMENTS = [
  {
    lead: true,
    text: "JCM is an enthusiastic and charismatic individual, and his actions demonstrate that he is kind-hearted towards those in need, reflecting his sense of responsibility as a person. His hands never waver when someone seeks help. His charismatic character strives to create a meaningful impact on society. He looks for opportunities to extend a helping hand to those who need it. As a human being, he attends to people\u2019s basic needs that are often neglected, and by offering support, he positively impacts their lives and demonstrates his true character.",
  },
  {
    kicker: "The businessman",
    text: "As a businessman, he has built a diversified conglomerate involved in real estate, construction, sports, hospitality, media, and other sectors. His passion for sports led him to establish Charles Sports Management, focusing on developing sports franchises and engaging youth in athletics, creating a lasting impact on sports, which he believes is an integral part of human life.",
  },
  {
    kicker: "The philosophy",
    text: "His business philosophy reflects entrepreneurship with purpose \u2014 his vision is not only about profit but also about social impact. He invests not just for financial gain but for the upliftment of people. While many focus solely on building empires, he runs businesses from sand to satellite, maintaining a people-centric approach that allows both his business and the community to grow together, setting him apart from other entrepreneurs.",
  },
  {
    kicker: "The family man",
    text: "As a family man, he is responsible, understanding, and caring \u2014 a devoted son, husband, and father who shares his values with his family. He positively impacts everything around him, demonstrating his true self throughout his journey.",
  },
];

/* Lifted from the philosophy paragraph below it. */
const PULL_QUOTE =
  "He runs businesses from sand to satellite, maintaining a people-centric approach that allows both his business and the community to grow together.";

export default function About() {
  return (
    <section id="about" className={styles.aboutSection}>
      <div className={styles.inner}>
        <SectionHeading
          eyebrow="Who he is"
          title="About Jose Charles Martin"
          lead="An entrepreneur whose ventures are measured less by what they earn than by who they lift."
        />

        <div className={styles.content}>
          <div className={styles.textSection}>
            <Reveal selector="[data-movement]" stagger={0.07} y={26}>
              {MOVEMENTS.map((movement, index) => (
                <React.Fragment key={movement.kicker ?? "lead"}>
                  <div
                    className={`${prose.movement} ${movement.lead ? prose.leadMovement : ""}`}
                    data-movement
                  >
                    {movement.kicker && (
                      <span className={prose.kicker}>
                        <span className={prose.kickerRule} aria-hidden="true" />
                        {movement.kicker}
                      </span>
                    )}
                    <p className={prose.paragraph}>{movement.text}</p>
                  </div>

                  {/* Sits just before the paragraph it is drawn from. */}
                  {index === 1 && (
                    <blockquote className={prose.pullQuote} data-movement>
                      <span className={prose.quoteMark} aria-hidden="true">
                        &ldquo;
                      </span>
                      <p>{PULL_QUOTE}</p>
                    </blockquote>
                  )}
                </React.Fragment>
              ))}
            </Reveal>

            <Reveal
              className={styles.features}
              selector="[data-pillar]"
              stagger={0.1}
              y={30}
            >
              {pillars.map((pillar) => (
                <article
                  className={styles.featureItem}
                  key={pillar.title}
                  data-pillar
                >
                  {/* Oversized, faint copy of the same mark, anchoring the
                      corner of the card. */}
                  <img
                    src={pillar.icon}
                    alt=""
                    aria-hidden="true"
                    className={styles.featureWatermark}
                  />

                  <span className={styles.iconBox}>
                    <img src={pillar.icon} alt="" width="24" height="24" />
                  </span>

                  <h3 className={styles.featureTitle}>{pillar.title}</h3>
                  <p className={styles.featureDesc}>{pillar.desc}</p>
                </article>
              ))}
            </Reveal>
          </div>

          <Reveal className={styles.imageSection} x={40} y={0} duration={1}>
            <figure className={styles.portrait}>
              <img src={MdImage} alt="Jose Charles Martin" className={styles.image} />
              <span className={styles.portraitAccent} aria-hidden="true" />
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
